import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  Chip,
  InputAdornment
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import { useParams, useNavigate } from 'react-router-dom';
import Header from './Header';
import { deleteUserAccount, submitDispute, getAuthToken, checkUserValidity } from './service';
import { SKU_DATA } from './constants'; 

export default function AccountPage() {
  const { userId } = useParams(); 
  const navigate = useNavigate();
  const contactEmail = "info@redish.com";

  const [pageLoading, setPageLoading] = useState(true);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  const [disputeSent, setDisputeSent] = useState(false);
  const [isSendingDispute, setIsSendingDispute] = useState(false);
  const [disputeError, setDisputeError] = useState(null);
  
  const [disputeData, setDisputeData] = useState({
    email: '',
    chargeDate: '',
    returnDate: '',
    amount: '',
    location: '',
    sku: []
  });

  useEffect(() => {
    async function validateUser() {
      setPageLoading(true);
      try {
        if (!userId) {
          navigate('/');
          return;
        }
        
        const token = await getAuthToken();
        const validation = await checkUserValidity(userId, token);
        
        if (!validation.isValid) {
          navigate('/');
        }
      } catch (err) {
        console.error(err);
      } finally {
        setPageLoading(false);
      }
    }
    validateUser();
  }, [userId, navigate]);

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleCloseDialog = () => {
    if (!isDeleting) {
      setOpenDeleteDialog(false);
      setDeleteError(null);
    }
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);

    try {
      const token = await getAuthToken();
      // Calls the API to wipe history
      await deleteUserAccount(userId, token);

      setShowSuccessToast(true);
      setOpenDeleteDialog(false);
      
      setTimeout(() => {
        navigate('/'); 
      }, 2000);
      
    } catch (err) {
      console.error(err);
      setDeleteError("We couldn't delete your history. Please try again or contact support.");
      setIsDeleting(false);
    }
  };

  const handleDisputeChange = (e) => {
    const { name, value } = e.target;
    setDisputeData(prev => ({
      ...prev,
      [name]: name === 'sku' ? (typeof value === 'string' ? value.split(',') : value) : value
    }));
  };

  const handleDisputeSubmit = async (e) => {
    e.preventDefault();
    setIsSendingDispute(true);
    setDisputeError(null);

    try {
      const token = await getAuthToken();
      
      const formattedData = {
        email: disputeData.email,
        charge_date: disputeData.chargeDate,
        return_date: disputeData.returnDate,
        amount_charged: parseFloat(disputeData.amount),
        location: disputeData.location,
        container_skus: disputeData.sku 
      };

      await submitDispute(userId, formattedData, token);
      setDisputeSent(true);

    } catch (err) {
      console.error(err);
      setDisputeError("Failed to submit dispute. Please try again or contact support.");
    } finally {
      setIsSendingDispute(false);
    }
  };

  const faqItems = [
    {
      question: "What is Re:Dish?",
      answer: (
        <Typography variant="body2">
          Re:Dish is a program that lets you enjoy your meal in high-quality reusable containers. Think of it like a library for food packaging: you check the container out at purchase, enjoy your food, and check it back in by returning the container to a <strong>Re:Dish bin</strong>.
        </Typography>
      )
    },
    {
      question: "How do I check out my container?",
      answer: (
        <>
          <Typography variant="body2" gutterBottom>It’s simple and happens right at the register:</Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="1. Scan" secondary="Place your reusable container over the DishLink scanner." />
            </ListItem>
            <ListItem>
              <ListItemText primary="2. Tap" secondary="Tap your Institution ID or credit card on the card reader." />
            </ListItem>
            <ListItem>
              <ListItemText primary="3. Go" secondary="Enjoy your meal!" />
            </ListItem>
          </List>
        </>
      )
    },
    {
      question: "Do I need to download an app?",
      answer: (
        <Typography variant="body2">
          <strong>No.</strong> You do not need an app or a login. You simply tap your ID or credit card at checkout. If you provide your cell number when prompted, we will send you helpful reminders via SMS to return your items within the allotted time.
        </Typography>
      )
    },
    {
      question: "How do I return my container?",
      answer: (
        <Typography variant="body2">
          Simply drop your used container into any designated <strong>Re:Dish bin</strong>. The Re:Dish team will scan the return, and it is automatically marked as "returned" in your account.
        </Typography>
      )
    },
    {
      question: "What happens if I forget to return it?",
      answer: (
        <Typography variant="body2">
          We will send you text message reminders before your due date. If it is late, you will be charged a fee.
        </Typography>
      )
    },
    {
      question: "Why do I have to tap my credit card?",
      answer: (
        <Typography variant="body2">
          We use a credit card tap to create a secure link to your account. <strong>We do not charge you to check out.</strong> You are only charged if you do not return the container within the allotted time.
        </Typography>
      )
    },
    {
      question: "Is my data safe?",
      answer: (
        <Typography variant="body2">
          Yes. If you use a credit card, your data is encrypted immediately by Stripe (a global leader in financial security). Re:Dish never sees or stores your actual credit card details. We collect your phone number so we can send reminders.
        </Typography>
      )
    },
    {
      question: "How do I unsubscribe from text messaging?",
      answer: (
        <>
          <Typography variant="body2" sx={{ mb: 2 }}>
              If you wish to stop receiving notifications, simply reply <strong>STOP</strong> to the text message thread from Re:Dish.
          </Typography>
          <Typography variant="body2">
            Please note that if you unsubscribe, we will not be able to remind you of the return due date for the items you have checked out. You will, however, still be able to use Re:Dish and check out items.
          </Typography>
        </>
      )
    },
    {
      question: "I returned my container but got charged. What do I do?",
      answer: (
        <Box>
          {!disputeSent ? (
            <form onSubmit={handleDisputeSubmit}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                We apologize for the error. Please provide the details below so we can investigate and refund the charge if applicable.
              </Typography>
              
              <TextField
                label="Email Address"
                type="email"
                name="email"
                value={disputeData.email}
                onChange={handleDisputeChange}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                required
                disabled={isSendingDispute}
              />

              <TextField
                label="Date of Charge"
                type="date"
                name="chargeDate"
                value={disputeData.chargeDate}
                onChange={handleDisputeChange}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                InputLabelProps={{ shrink: true }}
                required
                disabled={isSendingDispute}
              />

              <TextField
                label="Amount Charged"
                type="number"
                name="amount"
                value={disputeData.amount}
                onChange={handleDisputeChange}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                required
                disabled={isSendingDispute}
              />

              <TextField
                label="Date Returned"
                type="date"
                name="returnDate"
                value={disputeData.returnDate}
                onChange={handleDisputeChange}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                InputLabelProps={{ shrink: true }}
                required
                disabled={isSendingDispute}
              />

              <TextField
                label="Location Returned (e.g., Main Cafeteria)"
                type="text"
                name="location"
                value={disputeData.location}
                onChange={handleDisputeChange}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                required
                disabled={isSendingDispute}
              />

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="sku-select-label">Container(s)</InputLabel>
                <Select
                  labelId="sku-select-label"
                  name="sku"
                  multiple
                  value={disputeData.sku}
                  onChange={handleDisputeChange}
                  input={<OutlinedInput label="Container(s)" />}
                  disabled={isSendingDispute}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const option = SKU_DATA.find(o => o.value === value);
                        return <Chip key={value} label={option ? option.label : value} size="small" />;
                      })}
                    </Box>
                  )}
                  required
                >
                  {SKU_DATA.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {disputeError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {disputeError}
                </Alert>
              )}

              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                fullWidth
                disabled={isSendingDispute}
                startIcon={isSendingDispute ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {isSendingDispute ? "Submitting..." : "Submit Dispute"}
              </Button>
            </form>
          ) : (
            <Alert severity="success">
              Thank you. We have received your dispute and will contact you shortly via email.
            </Alert>
          )}
        </Box>
      )
    },
    {
      question: "How do I delete my history?",
      answer: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            If you wish to clear your data, you can delete your rental history permanently. 
            Note that this action cannot be undone.
          </Typography>
          <Button 
            variant="text" 
            color="error" 
            size="small"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            sx={{ fontWeight: 'bold', textTransform: 'none', px: 0 }}
          >
            Delete my history
          </Button>
        </Box>
      )
    }
  ];

  if (pageLoading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 2 }}>
      <Header userId={userId} />

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 'bold' }}>
          Frequently Asked Questions
        </Typography>
        
        {faqItems.map((item, index) => (
          <Accordion key={index} disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid #eee' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`panel${index}-content`}
              id={`panel${index}-header`}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                {item.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {item.answer}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      <Box sx={{ 
        mt: 6, 
        mb: 8, 
        textAlign: 'center', 
        bgcolor: 'grey.100', 
        p: 3, 
        borderRadius: 2 
      }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Need more help?
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          Contact Support:
        </Typography>
        <Typography variant="body1" color="primary">
          <a href={`mailto:${contactEmail}`} style={{ textDecoration: 'none', color: 'inherit' }}>
            {contactEmail}
          </a>
        </Typography>
      </Box>

      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Delete your history?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete your rental history? This action is permanent and cannot be undone.
          </DialogContentText>
          
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={handleCloseDialog} disabled={isDeleting} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained" 
            autoFocus
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isDeleting ? "Deleting..." : "Delete History"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={showSuccessToast} 
        autoHideDuration={6000} 
        onClose={() => setShowSuccessToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          History deleted successfully. Redirecting...
        </Alert>
      </Snackbar>

    </Container>
  );
}